"use client";

import { useState, useEffect, Suspense } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { createClientComponentClient } from "@supabase/auth-helpers-nextjs";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import posthog from 'posthog-js';
import { Provider } from "@supabase/supabase-js";

function LoginFormSkeleton() {
  return (
    <div className="animate-pulse space-y-4">
      <div className="h-10 bg-muted rounded"></div>
      <div className="h-10 bg-muted rounded"></div>
      <div className="h-10 bg-muted rounded"></div>
    </div>
  );
}

function LoginForm() {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [redirectUrl, setRedirectUrl] = useState(
    "https://community.aibuilderclub.com"
  );

  const searchParams = useSearchParams();
  const supabase = createClientComponentClient();
  const router = useRouter();

  useEffect(() => {
    const urlReturnParam = searchParams.get("returnUrl");
    if (urlReturnParam) {
      setRedirectUrl(urlReturnParam);
      console.log("Redirect URL:", redirectUrl);
    }
  }, [searchParams, redirectUrl]);

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setOtpSent(false);

    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
        },
      });

      if (error) {
        setError(error.message);
        return;
      }

      setOtpSent(true);
      setError(null);
    } catch (err: any) {
      setError("An unexpected error occurred while sending OTP");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setVerifying(true);
    setError(null);

    try {
      const { data: { session }, error } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: 'email',
      });

      if (error) {
        setError(error.message);
        setVerifying(false);
        return;
      }

      if (posthog?.__loaded && session?.user?.email) {
        posthog.capture('v2_login_successful_otp', {
          email: session.user.email,
          $set: {
            email: session.user.email,
            has_access: false // Will be updated after profile check
          }
        });
        
        // Ensure event is sent before redirect
        // Using a small delay to ensure event is sent since flush() may not be available in typings
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      if (session?.user?.email) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("has_access")
          .eq("email", session.user.email)
          .single();

        // Update user property if profile was found
        if (posthog?.__loaded && profile) {
          posthog.capture('$identify', {
            $set: {
              has_access: profile?.has_access || false
            }
          });
        }

        if (!profile?.has_access) {
          // Redirect to pricing if no access
          router.push("/pricing");
          return;
        }
      }

      // For successful authentication, keep verifying=true during redirection
      router.push(decodeURIComponent(redirectUrl));

    } catch (err: any) {
      setError("An unexpected error occurred during OTP verification");
      console.error(err);
      setVerifying(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get current session ID before redirect
      const sessionId = posthog?.__loaded ? posthog.get_session_id() : null;
      const distinctId = posthog?.__loaded ? posthog.get_distinct_id() : null;
      
      const redirectURL = new URL(window.location.origin + "/api/auth/callback");
      // Add session information to the redirect URL
      if (sessionId) redirectURL.searchParams.append('ph_sessionid', sessionId);
      if (distinctId) redirectURL.searchParams.append('ph_distinctid', distinctId);
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google' as Provider,
        options: {
          redirectTo: redirectURL.toString(),
        },
      });

      if (error) {
        setError(error.message);
        return;
      }

    } catch (err: any) {
      setError("An unexpected error occurred during Google sign-in");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {error && (
        <div className="rounded-md bg-destructive/15 p-4 text-sm text-red-500 mb-4">
          {error}
        </div>
      )}

      {!otpSent ? (
        <form onSubmit={handleSendOtp} className="space-y-4">
          {/* <Button
            type="button"
            variant="outline"
            className="w-full flex items-center justify-center gap-2"
            onClick={handleGoogleSignIn}
            disabled={loading}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            Sign in with Google
          </Button> */}

          {/* <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                Or continue with
              </span>
            </div>
          </div> */}

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@example.com"
              autoComplete="email"
              disabled={loading}
            />
          </div>

          <Button type="submit" disabled={loading} className="w-full">
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending OTP...
              </>
            ) : (
              "Join AI Builder Club"
            )}
          </Button>
        </form>
      ) : (
        <form onSubmit={handleVerifyOtp} className="space-y-4">
           <div className="rounded-md bg-green-50 p-4 text-sm text-green-600">
              Check your email ({email}) for the verification code.
           </div>
          <div className="space-y-2">
            <Label htmlFor="otp">Verification Code</Label>
            <Input
              id="otp"
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              required
              placeholder="Enter code from email"
              disabled={verifying}
              inputMode="numeric"
              autoComplete="one-time-code"
            />
          </div>
          <Button type="submit" disabled={verifying} className="w-full">
            {verifying ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Verifying...
              </>
            ) : (
              "Verify Code"
            )}
          </Button>
           <Button
              variant="link"
              type="button"
              onClick={() => {
                setOtpSent(false);
                setError(null);
                setOtp("");
              }}
              className="w-full text-sm"
              disabled={verifying}
            >
              Use a different email or resend code
            </Button>
        </form>
      )}
    </>
  );
}

// Move the auth check logic into a separate component
function AuthCheck() {
  const router = useRouter();
  const supabase = createClientComponentClient();
  const searchParams = useSearchParams();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      console.log("[/auth/signin AuthCheck] Checking for existing session...");
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session && session.user) {
          console.log("[/auth/signin AuthCheck] Found existing session for user:", session.user.email);
          
          // Check if user has access
          const { data: profile, error } = await supabase
            .from("profiles")
            .select("has_access")
            .eq("email", session.user.email)
            .single();
            
          if (error) {
            console.error("[/auth/signin AuthCheck] Error fetching profile:", error);
            // On error, we'll let the user stay on the sign-in page
            setIsCheckingAuth(false);
            return;
          }

          if (profile?.has_access === true) {
            // User has access - redirect to community or returnUrl
            const returnUrl = searchParams.get('returnUrl') || 'https://community.aibuilderclub.com';
            console.log("[/auth/signin AuthCheck] User has access. Redirecting to:", returnUrl);
            router.push(decodeURIComponent(returnUrl));
          } else {
            // User is logged in but doesn't have access - redirect to pricing
            console.log("[/auth/signin AuthCheck] User does NOT have access. Redirecting to /pricing");
            router.push('/pricing');
          }
        } else {
          console.log("[/auth/signin AuthCheck] No existing session found. Showing sign-in form.");
          setIsCheckingAuth(false);
        }
      } catch (err) {
        console.error("[/auth/signin AuthCheck] Error during auth check:", err);
        setIsCheckingAuth(false);
      }
    };

    checkAuth();
  }, [router, supabase, searchParams]);

  return isCheckingAuth ? (
    <div className="flex flex-col items-center justify-center py-4">
      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      <p className="mt-2 text-sm text-muted-foreground">Checking account status...</p>
    </div>
  ) : null;
}

// Main page component
export default function SignInPage() {
  useEffect(() => {
    if (posthog?.__loaded) {
      posthog.capture('v2_page_view_signin', {
        path: window.location.pathname,
        referrer: document.referrer,
        $set: {
          last_page_view: 'signin'
        }
      });
    }
  }, []);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center py-2">
      <main className="w-full max-w-md px-4">
        <Card>
          <CardHeader>
            <h1 className="text-2xl font-semibold text-center">Sign In to AI Builder Club</h1>
            <p className="text-sm text-muted-foreground text-center">
              Sign in to access your account
            </p>
          </CardHeader>

          <CardContent>
            <Suspense fallback={<LoginFormSkeleton />}>
              <AuthCheck />
              <LoginForm />
            </Suspense>
          </CardContent>

          <CardFooter className="flex justify-center">
            {/* <p className="text-sm text-muted-foreground">
              Don&apos;t have an account?{" "}
              <Link
                href="/auth/signup"
                className="text-primary hover:underline"
              >
                Sign up
              </Link>
            </p> */}
          </CardFooter>
        </Card>
      </main>
    </div>
  );
}
